import type { ReactElement } from "react";
import { Button, Callout, EmptyState, Spinner, Stack, Surface, Text } from "../ui-kit";
import type { AnalyticsDashboardState, AnalyticsQualification, AnalyticsSurface } from "./analyticsDataSource";
import styles from "./AnalyticsStatePanel.module.css";

export interface AnalyticsStatePanelProps {
  readonly state: Exclude<AnalyticsDashboardState, { readonly kind: "fixture-test-only" }>;
  readonly surface?: AnalyticsSurface;
}

/** The safe, value-free presentation for every non-fixture boundary state. */
export function AnalyticsStatePanel({ state, surface }: AnalyticsStatePanelProps): ReactElement | null {
  if (state.kind === "no-live-source") {
    return (
      <Surface as="section" padding="default" className={styles.panel}>
        <EmptyState
          title="Live analytics are not available yet"
          description="This dashboard will show product analytics when the reporting service is ready."
        />
      </Surface>
    );
  }

  if (state.kind === "loading") {
    return (
      <Surface as="section" padding="default" className={styles.panel} aria-busy="true">
        <Stack gap={3}>
          <Spinner label="Loading analytics…" showLabel size="lg" />
          {state.retry !== undefined ? <RetryButton retry={state.retry} disabled /> : null}
        </Stack>
      </Surface>
    );
  }

  if (state.kind === "range-no-data") {
    return (
      <Surface as="section" padding="default" className={styles.panel}>
        <EmptyState
          title="No analytics data for this period yet"
          description="Product analytics will appear after complete collection is available for the selected period."
        />
      </Surface>
    );
  }

  if (state.kind === "error") {
    return (
      <Surface as="section" padding="default" className={styles.panel}>
        <Stack gap={3}>
          <Callout tone="danger" role="alert" title="Analytics could not be loaded">
            <Text as="p">No analytics data is being shown. Try again.</Text>
          </Callout>
          <RetryButton retry={state.retry} />
        </Stack>
      </Surface>
    );
  }

  return state.kind === "live" && surface !== undefined ? <LiveSurfaceStatePanel state={state} surface={surface} /> : null;
}

function RetryButton({ retry, disabled = false }: { readonly retry: () => void; readonly disabled?: boolean }): ReactElement {
  return (
    <div>
      <Button
        aria-disabled={disabled || undefined}
        aria-label="Try loading analytics again"
        onClick={() => {
          if (!disabled) {
            retry();
          }
        }}
      >
        Try again
      </Button>
    </div>
  );
}

function LiveSurfaceStatePanel({
  state,
  surface,
}: {
  readonly state: Extract<AnalyticsDashboardState, { readonly kind: "live" }>;
  readonly surface: AnalyticsSurface;
}): ReactElement {
  const qualifiedSurface = state.dashboard.surfaces[surface];
  if (qualifiedSurface.qualification === "available") {
    return <QualifiedAggregate aggregate={qualifiedSurface.aggregate} context={state.dashboard.context} scopeLabel={qualifiedSurface.scopeLabel} />;
  }

  if (qualifiedSurface.qualification === "partial") {
    return <PartialAggregate aggregate={qualifiedSurface.aggregate} context={state.dashboard.context} scopeLabel={qualifiedSurface.scopeLabel} />;
  }

  const qualification = qualifiedSurface.qualification;
  const copy: Record<AnalyticsQualification, { title: string; body: string; tone: "info" | "warning" | "success" }> = {
    available: {
      title: "Analytics available",
      body: "This result is available for the selected period.",
      tone: "success",
    },
    partial: {
      title: "Includes only releasable data",
      body: "This result excludes data that cannot be released safely.",
      tone: "info",
    },
    suppressed: {
      title: "Data hidden to protect privacy",
      body: "There are too few underlying events to show this result safely.",
      tone: "info" as const,
    },
    incomplete: {
      title: "Incomplete analytics coverage",
      body: "Part of this period could not be collected. Values shown are not a complete picture.",
      tone: "warning" as const,
    },
    unavailable: {
      title: "Not available for this channel",
      body: "This metric is not collected for the selected channel.",
      tone: "info" as const,
    },
  };

  return (
    <Stack gap={4}>
      <Surface as="section" padding="default" className={styles.panel}>
        <Callout tone={copy[qualification].tone} title={copy[qualification].title}>
          <Text as="p">{copy[qualification].body}</Text>
        </Callout>
      </Surface>
      <Text as="p" variant="caption" role="status">
        <QualificationMetadata context={state.dashboard.context} scopeLabel={qualifiedSurface.scopeLabel} />
      </Text>
    </Stack>
  );
}

function PartialAggregate({
  aggregate,
  context,
  scopeLabel,
}: {
  readonly aggregate: ReactElement;
  readonly context: Extract<AnalyticsDashboardState, { readonly kind: "live" }>["dashboard"]["context"];
  readonly scopeLabel: string;
}): ReactElement {
  return (
    <QualifiedAggregate aggregate={aggregate} context={context} scopeLabel={scopeLabel}>
      <Callout tone="info" title="Includes only releasable data">
        <Text as="p">This result excludes data that cannot be released safely.</Text>
      </Callout>
    </QualifiedAggregate>
  );
}

function QualifiedAggregate({
  aggregate,
  context,
  scopeLabel,
  children,
}: {
  readonly aggregate: ReactElement;
  readonly context: Extract<AnalyticsDashboardState, { readonly kind: "live" }>["dashboard"]["context"];
  readonly scopeLabel: string;
  readonly children?: ReactElement;
}): ReactElement {
  return (
    <Stack gap={4}>
      <Surface as="section" padding="default" className={styles.panel}>
        <Stack gap={3}>
          {aggregate}
          {children}
        </Stack>
      </Surface>
      <Text as="p" variant="caption" role="status">
        <QualificationMetadata context={context} scopeLabel={scopeLabel} />
      </Text>
    </Stack>
  );
}

function QualificationMetadata({
  context,
  scopeLabel,
}: {
  readonly context: Extract<AnalyticsDashboardState, { readonly kind: "live" }>["dashboard"]["context"];
  readonly scopeLabel: string;
}): ReactElement {
  return (
    <>
      Analytics computed {context.computedAt}. Range {context.rangeLabel}. {context.granularityLabel}. {scopeLabel}. Coverage since {context.coverageStartAt}.
    </>
  );
}
